import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import { isEmpty, isNil } from 'lodash';
import { useDeviceType } from 'customHooks/useDeviceType';
import Utils from 'utilities/helper.util';
import { updateEnquiryById } from 'redux/enquiries';
import { selectIsFromSwxLocate } from 'redux/session/selectors';
import { updatingEnquiry } from 'redux/enquiries/selectors';
import { updateShowNotificationOptionsModal } from 'redux/user/actions';
import { phoneNumberFormatter } from 'utilities/validation';
import DefaultView from './Default';
import MobileView from './Mobile';

type Props = {
  page: string;
  parentPage?: string;
  enquiryDetail: any;
  headerTitle: string;
  showBackNavigation: boolean;
  screenSize: string;
  showGetPlansButton: boolean;
  handleGetPlansClick: Function;
  handleBackButtonClick: any;
  handleMobileJobDetailsClick: () => void;
  handleMobileDownloadAndToolsClick: () => void;
  isLoading: boolean;
  isModal: boolean;
  onClickClose?: any;
  responsesExpired: boolean;
  showResponsesExpiredModal: boolean;
  shareId: string;
  bodyDivRef?: any;
  setLualExtent: Function;
  onMiniMapLoaded?: Function;
};

const EnquiryInformation: React.FC<Props> = ({
  page,
  parentPage,
  enquiryDetail,
  headerTitle,
  showBackNavigation,
  screenSize,
  showGetPlansButton,
  handleGetPlansClick,
  handleBackButtonClick,
  handleMobileJobDetailsClick,
  handleMobileDownloadAndToolsClick,
  isLoading,
  isModal,
  onClickClose,
  responsesExpired,
  showResponsesExpiredModal,
  shareId,
  bodyDivRef,
  setLualExtent,
  onMiniMapLoaded
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isUpdatingEnquiry: any = useSelector(updatingEnquiry);
  const isFromSwxLocate = useSelector(selectIsFromSwxLocate);
  const [showMapModal, setShowMapModal] = useState<boolean>(false);
  const [mapModalLoaded, setMapModalLoaded] = useState<boolean>(false);
  const [showDetails, setShowDetails] = useState<boolean>(true);
  const [scrollY, setScrollY] = useState<any>(window.scrollY);
  const [forceShowDetails, setForceShowDetails] = useState<boolean>(false);
  const [showAutoRenewOption, setShowAutoRenewOption] = useState<boolean>(
    false
  );
  const [autoRenewingStatus, setAutoRenewingStatus] = useState<string>('On');
  const [isEnquiryCreatedToday, setIsEnquiryCreatedToday] = useState<boolean>(
    true
  );
  const [showMoreDetails, setShowMoreDetails] = useState<boolean>(false);
  const [pageHeight, setPageHeight] = useState<number>(0);
  const [screenHeight, setScreenHeight] = useState<number>(0);

  const { isMobileOrTablet } = useDeviceType();

  const handleScrolling = useCallback((e) => {
    const window = e.currentTarget;
    setTimeout(() => {
      setForceShowDetails(false);
    }, 50);
    setScrollY(window.scrollTop || window.scrollY);
  }, []);

  let theApp: any = isMobileOrTablet ? document.getElementById('app') : window;
  if (isModal) {
    const enquiryDetailsModal = document.getElementsByClassName(
      'enquiry-details-modal'
    )?.[0];
    theApp = enquiryDetailsModal?.getElementsByClassName(
      'bx--modal-content'
    )?.[0];
  }

  useEffect(() => {
    setScrollY(window.scrollY);
    setForceShowDetails(false);
    theApp?.addEventListener('scroll', handleScrolling);
    return () => {
      theApp?.removeEventListener('scroll', handleScrolling);
    };
  }, [handleScrolling, theApp]);

  useEffect(() => {
    if (isMobileOrTablet) {
      return;
    }

    if (scrollY > 0) {
      const heightDiff = pageHeight - screenHeight;
      if (heightDiff > 160) {
        if (!forceShowDetails) {
          setShowDetails(false);
        } else {
          setShowDetails(true);
        }
      }
    } else {
      setForceShowDetails(false);
      setShowDetails(true);
    }
    // eslint-disable-next-line
  }, [scrollY]);

  const handleModalState = () => {
    setShowMapModal(!showMapModal);
  };

  useEffect(() => {
    if (enquiryDetail) {
      if (enquiryDetail.autoRenewUntil && enquiryDetail.nextAutoRenewAt) {
        setShowAutoRenewOption(true);
      }

      if (
        enquiryDetail?.autoRenewUntil &&
        !enquiryDetail?.isAutoRenewComplete
      ) {
        setAutoRenewingStatus('On');
      } else if (enquiryDetail?.isAutoRenewComplete) {
        setAutoRenewingStatus('Completed');
      } else {
        setAutoRenewingStatus('Off');
      }

      if (enquiryDetail?.createdAt) {
        const createdAt = dayjs(enquiryDetail.createdAt);
        const today = dayjs();
        setIsEnquiryCreatedToday(createdAt.isSame(today, 'day'));
      }
    }
  }, [enquiryDetail]);

  useEffect(() => {
    if (!isMobileOrTablet) {
      const screenHeight = window.innerHeight;
      setScreenHeight(screenHeight);

      if (bodyDivRef?.current && !isLoading) {
        const pageHeight = bodyDivRef.current.scrollHeight;
        setPageHeight(pageHeight);
      }
    }
  }, [
    enquiryDetail,
    isMobileOrTablet,
    showMoreDetails,
    showGetPlansButton,
    bodyDivRef,
    isLoading
  ]);

  const handleDuplicateEnquiry = (enquiryId: string) => {
    let url = `/enquiries/new?clone=${enquiryId}`;

    if (shareId) {
      url += `&shareId=${shareId}`;
    }
    history.push(url);
  };

  const toggleDetailsPanel = () => {
    setForceShowDetails(true);
    setShowDetails(true);
  };

  const toggleNotificationOptions = (val: boolean) => {
    dispatch(updateShowNotificationOptionsModal(val));
  };

  const offEnquiryAutoRenew = () => {
    if (enquiryDetail?.id) {
      dispatch(
        updateEnquiryById({
          enquiryId: enquiryDetail.id,
          autoRenewUntil: null,
          nextAutoRenewAt: null
        })
      );
    }
  };

  const backButtonClick = () => {
    handleBackButtonClick();
  };

  const toggleShowMoreDetailsSection = () => {
    setShowMoreDetails(!showMoreDetails);
  };

  const splitArrayToNextLine = (data: any) => {
    return data.map((item: any, index: number) => (
      <React.Fragment key={index}>
        {item}
        {index < data.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const splitToNextLine = (data: string) => {
    if (isEmpty(data)) {
      return '';
    }
    return data.split(',').map((item: string, index: number) => (
      <React.Fragment key={index}>
        {item.trim()}
        <br />
      </React.Fragment>
    ));
  };

  const formatEnquirerDetail = (enquirer: any) => {
    return (
      <React.Fragment>
        {enquirer.firstName} {enquirer.lastName} <br />
        {enquirer.companyName && (
          <React.Fragment>
            {enquirer.companyName}
            <br />
          </React.Fragment>
        )}
        {`${Utils.getFullAddress(enquirer.Address)}`} <br />
        {enquirer.email} <br />
        {!isNil(enquirer.phoneNumber) && (
          <React.Fragment>
            {phoneNumberFormatter({
              phoneNumber: enquirer.phoneNumber
            }).getNationalNumberFormat()}
            <br />
          </React.Fragment>
        )}
      </React.Fragment>
    );
  };

  const sharedProps = {
    enquiryDetail,
    handleGetPlansClick,
    handleBackButtonClick: backButtonClick,
    isLoading,
    isModal,
    onClickClose,
    responsesExpired,
    showResponsesExpiredModal,
    setLualExtent,
    isFromSwxLocate
  };

  const mobileViewProps = {
    ...sharedProps,
    page,
    parentPage,
    handleMobileJobDetailsClick,
    handleMobileDownloadAndToolsClick
  };

  const defaultViewProps = {
    ...sharedProps,
    headerTitle,
    showBackNavigation,
    screenSize,
    showGetPlansButton,
    shareId,
    bodyDivRef,
    onMiniMapLoaded,
    isUpdatingEnquiry,
    showMapModal,
    mapModalLoaded,
    showDetails,
    showAutoRenewOption,
    autoRenewingStatus,
    isEnquiryCreatedToday,
    showMoreDetails,

    handleModalState,
    setMapModalLoaded,
    handleDuplicateEnquiry,
    toggleDetailsPanel,
    toggleNotificationOptions,
    offEnquiryAutoRenew,
    toggleShowMoreDetailsSection,
    splitArrayToNextLine,
    splitToNextLine,
    formatEnquirerDetail
  };

  if (isMobileOrTablet) {
    return <MobileView {...mobileViewProps} />;
  }

  return <DefaultView {...defaultViewProps} />;
};

export default EnquiryInformation;
